import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MessageSquare } from 'lucide-react';
import { ContactDialog } from '../components/ContactDialog';
import { ReviewDialog } from '../components/ReviewDialog';
import { ReviewCard } from '../components/ReviewCard';
import { StarRating } from '../components/StarRating';
import { SEO } from '../components/SEO';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [contactOpen, setContactOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [seoSettings, setSeoSettings] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    fetchSEOSettings();
    fetchSiteContent();
    fetchPageContent();
    fetchReviews();
    fetchReviewsStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/approved`);
      setReviews(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchReviewsStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setReviewsStats(response.data);
    } catch (error) {
      console.error('Error fetching reviews stats:', error);
    }
  };

  const fetchSiteContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`);
      setSiteContent(response.data);
    } catch (error) {
      console.error('Error fetching site content:', error);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.home) {
        setPageContent(response.data.pages.home);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const fetchSEOSettings = async () => {
    try {
      const response = await axios.get(`${API}/seo-settings`);
      setSeoSettings(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    }
  };

  // Хелпер для получения текста - сначала проверяет pageContent (новый), потом siteContent (старый)
  const getText = (field, fallbackRu, fallbackLv) => {
    // Сначала проверяем новый pages-content
    if (pageContent && pageContent[field]) {
      if (typeof pageContent[field] === 'object') {
        return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return pageContent[field];
    }
    // Затем старый site-content
    if (siteContent && siteContent[field]) {
      if (typeof siteContent[field] === 'object') {
        return siteContent[field][lang] || siteContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return siteContent[field];
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title={seoSettings?.[`meta_title_${lang}`]}
        description={seoSettings?.[`meta_description_${lang}`]}
        keywords={seoSettings?.[`meta_keywords_${lang}`]}
      />
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 animate-ken-burns">
            <img
              src="https://images.pexels.com/photos/19896144/pexels-photo-19896144.jpeg?auto=compress&cs=tinysrgb&w=1920&dpr=2"
              alt="Luxury Diamond Engagement Ring"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          
          {/* Дополнительный блеск для эффекта бриллианта */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-accent/20 rounded-full blur-3xl animate-pulse animation-delay-1000" />
            <div className="absolute bottom-1/4 right-1/4 w-36 h-36 bg-white/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
          className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto pt-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-6 flex justify-center"
          >
            <img 
              src="/images/logo.png" 
              alt="Pure Atelier Riga Logo" 
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
            />
          </motion.div>
          
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl mb-6 tracking-tight" data-testid="hero-title">
            {getText('hero_title', 'Pure Atelier Riga', 'Pure Atelier Riga')}
          </h1>
          <p className="font-accent text-xl md:text-2xl mb-8 italic" data-testid="hero-subtitle">
            {getText('hero_subtitle', 'Fine Jewelry & Engagement Rings', 'Fine Jewelry & Engagement Rings')}
          </p>
          <p className="font-body text-base md:text-lg mb-12 max-w-2xl mx-auto tracking-wide">
            {getText('hero_description', 
              'Эксклюзивная коллекция ювелирных изделий для особенных моментов. Каждое украшение — это история любви, воплощённая в драгоценных камнях.',
              'Ekskluzīva juvelierizstrādājumu kolekcija īpašiem brīžiem. Katra rotaslieta ir mīlestības stāsts, kas iemiesots dārgakmeņos.'
            )}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/gallery">
              <Button 
                size="lg"
                className="bg-white text-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
                data-testid="hero-gallery-button"
              >
                {t('hero.viewCollection')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setContactOpen(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-foreground uppercase tracking-widest transition-colors duration-500"
              data-testid="hero-contact-button"
            >
              {t('hero.contact')}
            </Button>
          </div>
        </motion.div>
      </section>

      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto" data-testid="reviews-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Star className="w-8 h-8 text-accent" fill="#C9A961" />
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight" data-testid="reviews-title">
              {getText('reviews_title', 'Отзывы клиентов', 'Klientu atsauksmes')}
            </h2>
          </div>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <StarRating rating={5} size={24} />
              <span className="font-heading text-3xl text-accent">{reviewsStats.average_rating}</span>
            </div>
            <span className="text-muted-foreground">
              {lang === 'lv' 
                ? `${reviewsStats.total_reviews} atsauksmes` 
                : `${reviewsStats.total_reviews} отзывов`}
            </span>
          </div>

          <p className="font-body text-muted-foreground max-w-2xl mx-auto tracking-wide">
            {getText('reviews_description',
              'Наши клиенты делятся своим опытом с украшениями Pure Atelier',
              'Mūsu klienti dalās savā pieredzē ar Pure Atelier juvelierizstrādājumiem'
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {reviews.map((review, index) => (
            <ReviewCard key={review.id} review={review} index={index} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <Button 
            size="lg"
            onClick={() => setReviewOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
            data-testid="leave-review-button"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            {lang === 'lv' ? 'Atstāt atsauksmi' : 'Оставить отзыв'}
          </Button>
        </motion.div>
      </section>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  );
};
